import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckIcon, GiveIcon, SearchIcon } from "../assets/icons/icons";
import LandingFooter from "../components/LandingFooter";
import { trackGoal } from "../utils/datafast";
import useFocusTrap from "../hooks/useFocusTrap";
import "./PublicSite.css";

const LOGO_WHITE_PUBLIC_PATH = "/clearbadge-logo-white.png";
const API_BASE = process.env.REACT_APP_API_URL || "";

function CalendarIcon(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={props.className}
      onClick={props.onClick}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 2v4" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 2v4" />
      <rect width="18" height="18" x="3" y="4" rx="2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18" />
    </svg>
  );
}

const PROGRAM_HIGHLIGHTS = [
  {
    icon: GiveIcon,
    iconClassName: "step-icon-fill",
    title: "25% recurring commission",
    description: "Earn 25% of subscription revenue from every paid customer you refer.",
  },
  {
    icon: CalendarIcon,
    title: "Paid monthly for up to 12 months",
    description: "Keep earning while each referred customer remains active during their first year.",
  },
  {
    icon: SearchIcon,
    iconClassName: "step-icon-fill",
    title: "60-day referral window",
    description: "You get credit when someone signs up within 60 days of clicking your link.",
  },
  {
    icon: CheckIcon,
    iconProps: { fill: "currentColor" },
    title: "No caps, no minimums",
    description: "There is no ceiling on commissions and no minimum performance threshold to stay in the program.",
  },
];

const HOW_IT_WORKS = [
  {
    title: "Apply to join",
    description: "Tell us about your audience, channels, and how you plan to promote ClearBadge.",
  },
  {
    title: "Share your affiliate link",
    description: "Promote ClearBadge to professionals and audiences that care about trust, verification, and credibility.",
  },
  {
    title: "Earn monthly commissions",
    description: "Get paid monthly on referred paid customers for up to 12 months per referral.",
  },
];

export default function AffiliatePage() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [sendingApplication, setSendingApplication] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState("idle");
  const [applicationMessage, setApplicationMessage] = useState("");
  const [applicationForm, setApplicationForm] = useState({
    fullName: "",
    email: "",
    audience: "",
    notes: "",
  });
  const [showFloatingNav, setShowFloatingNav] = useState(true);
  const landingRootRef = useRef(null);
  const mobileDrawerRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);
  const applyModalRef = useRef(null);
  const applyModalCloseRef = useRef(null);
  const applyModalInitialFocusRef = useRef(null);
  const applyModalReturnFocusRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const isVerifyPage = location.pathname === "/";
  const isFindProPage =
    location.pathname.startsWith("/find-a-pro") || location.pathname.startsWith("/find-pro");
  const isPricingPage = location.pathname.startsWith("/pricing");
  const isAboutPage = location.pathname.startsWith("/about");
  const isAffiliatePage = location.pathname.startsWith("/affiliate");
  const isPrivacyPage = location.pathname.startsWith("/privacy-policy");
  const isTermsPage = location.pathname.startsWith("/terms-of-service");

  useFocusTrap({
    isOpen: mobileMenuOpen,
    containerRef: mobileDrawerRef,
    onClose: () => setMobileMenuOpen(false),
    returnFocusRef: mobileMenuButtonRef,
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: applyModalOpen,
    containerRef: applyModalRef,
    onClose: () => setApplyModalOpen(false),
    initialFocusRef: applyModalInitialFocusRef,
    returnFocusRef: applyModalReturnFocusRef,
    lockScroll: true,
  });

  useEffect(() => {
    trackGoal("view_affiliate_program");
  }, []);

  useEffect(() => {
    const rootNode = landingRootRef.current;
    if (!rootNode) return undefined;
    const revealNodes = rootNode.querySelectorAll("[data-reveal]");
    if (!revealNodes.length) return undefined;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      revealNodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -10% 0px" }
    );

    revealNodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    lastScrollYRef.current = window.scrollY || 0;

    const onScroll = () => {
      const currentY = window.scrollY || 0;
      const previousY = lastScrollYRef.current;
      const delta = currentY - previousY;
      if (Math.abs(delta) < 6) return;

      if (currentY <= 24) {
        setShowFloatingNav(true);
      } else if (delta > 0) {
        setShowFloatingNav(false);
      } else {
        setShowFloatingNav(true);
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) setShowFloatingNav(true);
  }, [mobileMenuOpen]);

  const openApplyModal = (event) => {
    applyModalReturnFocusRef.current = event?.currentTarget || null;
    setApplicationStatus("idle");
    setApplicationMessage("");
    setApplyModalOpen(true);
  };

  const closeApplyModal = () => {
    if (sendingApplication) return;
    setApplyModalOpen(false);
  };

  const handleApplicationFieldChange = (field) => (event) => {
    const value = event.target.value;
    setApplicationForm((current) => ({ ...current, [field]: value }));
  };

  const submitAffiliateApplication = async (event) => {
    event.preventDefault();
    if (sendingApplication) return;

    setSendingApplication(true);
    setApplicationStatus("idle");
    setApplicationMessage("");

    try {
      const res = await fetch(`${API_BASE}/api/affiliate-application`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(applicationForm),
      });
      const payload = await res.json().catch(() => ({}));

      if (!res.ok) {
        setApplicationStatus("error");
        setApplicationMessage(payload?.error || "Unable to submit affiliate application.");
        return;
      }

      setApplicationStatus("success");
      setApplicationMessage(
        payload?.message || "Application received. Our team will review it and follow up soon."
      );
      setApplicationForm({
        fullName: "",
        email: "",
        audience: "",
        notes: "",
      });
      trackGoal("affiliate_application_submitted");
    } catch (_err) {
      setApplicationStatus("error");
      setApplicationMessage("Unable to submit affiliate application.");
    } finally {
      setSendingApplication(false);
    }
  };

  return (
    <div ref={landingRootRef} className="public-shell landing affiliate-page clearbadge-public">
      <header className={`public-topbar landing-topbar ${showFloatingNav ? "" : "landing-topbar--hidden"}`}>
        <div className="landing-topbar-inner">
          <div className="landing-topbar-left">
            <Link to="/" className="brand-inline" aria-label="ClearBadge home">
              <img src={LOGO_WHITE_PUBLIC_PATH} alt="ClearBadge logo" className="brand-logo" />
            </Link>
            <nav className="public-search-nav" aria-label="Search navigation">
              <Link
                className={`top-link ${location.pathname === "/" ? "active" : ""}`}
                to="/"
                data-reveal
                style={{ "--reveal-delay": "560ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Verify a License
              </Link>
              <Link
                className={`top-link ${location.pathname.startsWith("/find-a-pro") ? "active" : ""}`}
                to="/find-a-pro"
                data-reveal
                style={{ "--reveal-delay": "700ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Find a Pro
              </Link>
              <Link
                className={`top-link ${location.pathname.startsWith("/pricing") ? "active" : ""}`}
                to="/pricing"
                data-reveal
                style={{ "--reveal-delay": "840ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Pricing
              </Link>
              <Link
                className={`top-link ${location.pathname.startsWith("/about") ? "active" : ""}`}
                to="/about"
                data-reveal
                style={{ "--reveal-delay": "980ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                About
              </Link>
            </nav>
          </div>
          <div className="public-links landing-topbar-right">
            <Link
              className="top-link"
              to="/login"
              data-reveal
              style={{ "--reveal-delay": "1120ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
            >
              Sign In
            </Link>
            <button
              className="primary nav-cta mobile-signup"
              type="button"
              onClick={openApplyModal}
              data-reveal
              style={{ "--reveal-delay": "1260ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
            >
              Apply Now
            </button>
            <button
              ref={mobileMenuButtonRef}
              className="icon-btn mobile-menu-btn"
              type="button"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
              aria-controls="affiliate-mobile-menu"
              onClick={() => setMobileMenuOpen((open) => !open)}
            >
              <span className="burger-line" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <div className={`public-mobile-overlay ${mobileMenuOpen ? "open" : ""}`}>
        <button
          type="button"
          className="public-mobile-overlay-dismiss"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Close menu"
        />
        <div
          id="affiliate-mobile-menu"
          ref={mobileDrawerRef}
          className={`public-mobile-drawer ${mobileMenuOpen ? "open" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="public-mobile-header">
            <Link to="/" className="brand-inline" aria-label="ClearBadge home" onClick={() => setMobileMenuOpen(false)}>
              <img src={LOGO_WHITE_PUBLIC_PATH} alt="ClearBadge logo" className="brand-logo" />
            </Link>
            <button className="icon-btn mobile-drawer-close" type="button" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)}>
              <span className="mobile-drawer-close-icon" aria-hidden="true" />
            </button>
          </div>
          <div className="public-mobile-links">
            <Link to="/login" className="public-mobile-link" onClick={() => setMobileMenuOpen(false)}>
              Sign In
            </Link>
            <button
              type="button"
              onClick={openApplyModal}
              className="public-mobile-link primary"
              onClickCapture={() => setMobileMenuOpen(false)}
            >
              Apply Now
            </button>
            <div className="mobile-menu-section">
              <span className="mobile-menu-label">TOOLS</span>
              <Link
                to="/"
                className={`public-mobile-link secondary ${isVerifyPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Verify a License</span>
              </Link>
              <Link
                to="/find-a-pro"
                className={`public-mobile-link secondary ${isFindProPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Find a Pro</span>
              </Link>
            </div>
            <div className="mobile-menu-section">
              <span className="mobile-menu-label">RESOURCES</span>
              <Link
                to="/about"
                className={`public-mobile-link secondary ${isAboutPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">About</span>
              </Link>
              <Link
                to="/pricing"
                className={`public-mobile-link secondary ${isPricingPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Pricing</span>
              </Link>
              <Link
                to="/affiliate"
                className={`public-mobile-link secondary ${isAffiliatePage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Affiliate Program</span>
              </Link>
              <Link
                to="/privacy-policy"
                className={`public-mobile-link secondary ${isPrivacyPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Privacy Policy</span>
              </Link>
              <Link
                to="/terms-of-service"
                className={`public-mobile-link secondary ${isTermsPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Terms of Service</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <main className="landing-main affiliate-main" id="main-content">
        <section className="hero landing-hero affiliate-hero">
          <div className="hero-inner affiliate-hero-inner">
            <div className="affiliate-hero-copy">
              <div className="trust-pill" data-reveal style={{ "--reveal-delay": "1420ms" }}>
                Affiliate Program
              </div>
              <h1 data-reveal style={{ "--reveal-delay": "1580ms" }}>
                Promote Trust, Earn Passive Income
              </h1>
              <p className="hero-lead" data-reveal style={{ "--reveal-delay": "1740ms" }}>
                Join the ClearBadge affiliate program and earn 25% commission on every paid customer you refer.
              </p>
              <div className="affiliate-hero-actions" data-reveal style={{ "--reveal-delay": "1900ms" }}>
                <button type="button" className="primary get-started-primary" onClick={openApplyModal}>
                  Apply to Become an Affiliate
                </button>
                <Link className="get-started-secondary" to="/pricing">
                  View Pricing
                </Link>
              </div>
              <p className="muted affiliate-hero-note" data-reveal style={{ "--reveal-delay": "2060ms" }}>
                It takes less than 2 minutes to apply.
              </p>
            </div>
          </div>
        </section>

        <section className="about-section about-mission how-section built-section affiliate-how-section">
          <div className="landing-container">
            <div className="built-head">
              <p
                className="eyebrow how-it-works-eyebrow"
                data-reveal
                style={{ "--reveal-delay": "0ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}
              >
                HOW IT WORKS
              </p>
              <h2 data-reveal style={{ "--reveal-delay": "140ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}>
                Start promoting ClearBadge in <span className="how-title-gradient">three simple steps</span>
              </h2>
              <p data-reveal style={{ "--reveal-delay": "280ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}>
                The program is straightforward: apply, share ClearBadge with the right audience, and earn recurring
                monthly commissions as referrals convert.
              </p>
            </div>

            <div className="built-card-grid">
              {HOW_IT_WORKS.map((item, idx) => (
                <article
                  key={item.title}
                  className="built-card"
                  data-reveal
                  style={{ "--reveal-delay": `${idx * 110}ms` }}
                >
                  <div className="built-card-meta" aria-hidden="true">
                    <span className="built-step-pill">{idx + 1}</span>
                    <span className="built-card-corner-dot" />
                  </div>
                  <h3>{item.title}</h3>
                  <p className="muted">{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="about-section about-mission how-section built-section affiliate-program-section">
          <div className="landing-container">
            <div className="built-head">
              <p
                className="eyebrow how-it-works-eyebrow"
                data-reveal
                style={{ "--reveal-delay": "0ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}
              >
                AFFILIATE PROGRAM HIGHLIGHTS
              </p>
              <h2 data-reveal style={{ "--reveal-delay": "140ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}>
                A recurring revenue offer built for <span className="how-title-gradient">serious affiliate partners</span>
              </h2>
              <p data-reveal style={{ "--reveal-delay": "280ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}>
                Promote a trust-first product with a clear value proposition, strong recurring economics, and terms
                built for creators, consultants, educators, and communities with professional audiences.
              </p>
            </div>

            <div className="built-card-grid">
              {PROGRAM_HIGHLIGHTS.map((item, idx) => (
                <article
                  key={item.title}
                  className="built-card"
                  data-reveal
                  style={{ "--reveal-delay": `${idx * 110}ms` }}
                >
                  <div className="affiliate-tile-head">
                    <div className="built-card-icon">
                      <item.icon
                        className={`step-icon ${item.iconClassName || ""}`.trim()}
                        {...(item.iconProps || {})}
                      />
                    </div>
                    <h3>{item.title}</h3>
                  </div>
                  <p className="muted">{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="get-started-section affiliate-cta-section" id="get-started">
          <div className="landing-container">
            <div className="get-started-card" data-reveal>
              <div className="get-started-copy">
                <p className="eyebrow">READY TO APPLY?</p>
                <h2>Apply to become a ClearBadge affiliate.</h2>
                <p>
                  If you reach professionals who care about trust, credibility, and verification, this program is
                  built to convert and pay recurring commissions.
                </p>
              </div>
              <div className="get-started-actions">
                <button type="button" className="primary get-started-primary" onClick={openApplyModal}>
                  Apply to Become an Affiliate
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {applyModalOpen && (
        <div className="affiliate-apply-modal-backdrop" onClick={closeApplyModal}>
          <div
            ref={applyModalRef}
            className="affiliate-apply-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="affiliate-apply-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="affiliate-apply-modal-header">
              <div>
                <h3 id="affiliate-apply-modal-title">Apply to Become an Affiliate</h3>
                <p>Send a short application directly to the ClearBadge team.</p>
              </div>
              <button
                ref={applyModalCloseRef}
                type="button"
                className="affiliate-apply-modal-close"
                onClick={closeApplyModal}
                aria-label="Close affiliate application modal"
              >
                x
              </button>
            </div>

            <form className="affiliate-apply-modal-body" onSubmit={submitAffiliateApplication}>
              <div className="affiliate-apply-form-grid">
                <div className="affiliate-apply-field">
                  <label htmlFor="affiliate-apply-full-name">Full name</label>
                  <input
                    ref={applyModalInitialFocusRef}
                    id="affiliate-apply-full-name"
                    type="text"
                    className="affiliate-apply-input"
                    placeholder="Jane Smith"
                    value={applicationForm.fullName}
                    onChange={handleApplicationFieldChange("fullName")}
                    disabled={sendingApplication || applicationStatus === "success"}
                    required
                  />
                </div>

                <div className="affiliate-apply-field">
                  <label htmlFor="affiliate-apply-email">Email</label>
                  <input
                    id="affiliate-apply-email"
                    type="email"
                    className="affiliate-apply-input"
                    placeholder="jane@example.com"
                    value={applicationForm.email}
                    onChange={handleApplicationFieldChange("email")}
                    disabled={sendingApplication || applicationStatus === "success"}
                    required
                  />
                </div>
              </div>

              <div className="affiliate-apply-field">
                <label htmlFor="affiliate-apply-audience">Audience, website, or channels</label>
                <input
                  id="affiliate-apply-audience"
                  type="text"
                  className="affiliate-apply-input"
                  placeholder="Newsletter, YouTube, coaching audience, website, etc."
                  value={applicationForm.audience}
                  onChange={handleApplicationFieldChange("audience")}
                  disabled={sendingApplication || applicationStatus === "success"}
                  required
                />
              </div>

              <div className="affiliate-apply-field">
                <label htmlFor="affiliate-apply-notes">Additional notes</label>
                <textarea
                  id="affiliate-apply-notes"
                  className="affiliate-apply-input affiliate-apply-textarea"
                  placeholder="Anything helpful about your audience or how you’d promote ClearBadge."
                  value={applicationForm.notes}
                  onChange={handleApplicationFieldChange("notes")}
                  disabled={sendingApplication || applicationStatus === "success"}
                  rows={5}
                />
              </div>

              {applicationStatus === "success" && (
                <div className="affiliate-apply-feedback success" role="status" aria-live="polite">
                  <span className="affiliate-apply-feedback-icon" aria-hidden="true">✓</span>
                  <span>{applicationMessage}</span>
                </div>
              )}

              {applicationStatus === "error" && (
                <div className="affiliate-apply-feedback error" role="alert">
                  <span className="affiliate-apply-feedback-icon" aria-hidden="true">x</span>
                  <span>{applicationMessage}</span>
                </div>
              )}

              <div className="affiliate-apply-actions">
                <button type="button" className="ghost" onClick={closeApplyModal} disabled={sendingApplication}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary"
                  disabled={sendingApplication || applicationStatus === "success"}
                >
                  {sendingApplication ? "Submitting..." : "Submit Application"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <LandingFooter />
    </div>
  );
}
